import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Button, Table, Badge, Spinner, Modal } from "react-bootstrap";
import { PlusCircle, PencilSquare, Trash, Image, CartPlus, CartDash } from "react-bootstrap-icons";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import PrintItemService from "../../services/printitem.service";
import { getSubsite } from "../../reducers/auth";

const apiUrl = process.env.REACT_APP_LOCALHOST_URL;

const getImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    if (url.includes('uploads')) return apiUrl + url;
    return url;
};

const AdminPrintItems = () => {
    const currentSubsite = useSelector(getSubsite);
    const [printItems, setPrintItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const [catalogBusyId, setCatalogBusyId] = useState(null);

    useEffect(() => {
        loadPrintItems();
    }, [currentSubsite?.id]);

    const loadPrintItems = async () => {
        try {
            setLoading(true);
            const items = await PrintItemService.getPrintItemsForSubsiteCatalog();
            setPrintItems(items);
            setError(null);
        } catch (err) {
            console.error("Error loading print items:", err);
            setError("Failed to load print items");
        } finally {
            setLoading(false);
        }
    };

    const handleToggleCatalog = async (item) => {
        try {
            setCatalogBusyId(item.id);
            if (item.inCatalog) {
                await PrintItemService.removePrintItemFromCatalog(item.id);
            } else {
                await PrintItemService.addPrintItemToCatalog(item.id);
            }
            setPrintItems(printItems.map(pi =>
                pi.id === item.id ? { ...pi, inCatalog: !item.inCatalog } : pi
            ));
        } catch (err) {
            console.error("Error toggling catalog membership:", err);
            alert("Failed to update catalog for this subsite");
        } finally {
            setCatalogBusyId(null);
        }
    };

    const handleDeleteClick = (item) => {
        setItemToDelete(item);
        setShowDeleteModal(true);
    };

    const confirmDelete = async () => {
        if (!itemToDelete) return;
        try {
            await PrintItemService.deletePrintItem(itemToDelete.id);
            setPrintItems(printItems.filter(item => item.id !== itemToDelete.id));
            setShowDeleteModal(false);
            setItemToDelete(null);
        } catch (err) {
            console.error("Error deleting print item:", err);
            alert("Failed to delete print item");
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(amount || 0);
    };

    const getPricingTypeLabel = (pricingType) => {
        switch (pricingType) {
            case 2: return 'Quantity';
            case 3: return 'Bill Later';
            default: return 'Flat';
        }
    };

    const formatPriceDisplay = (item) => {
        if (item.pricingType === 3) {
            return <span className="text-muted">Bill Later</span>;
        }
        if (item.pricingType === 2 && item.print_item_quantity_prices?.length > 0) {
            const prices = item.print_item_quantity_prices;
            const min = Math.min(...prices.map(p => p.price));
            const max = Math.max(...prices.map(p => p.price));
            return min === max ? formatCurrency(min) : `${formatCurrency(min)} - ${formatCurrency(max)}`;
        }
        return formatCurrency(item.price);
    };

    if (loading) {
        return (
            <Container className="py-4 text-center">
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
            </Container>
        );
    }

    return (
        <Container className="py-4">
            <Row className="mb-4 align-items-center">
                <Col>
                    <h2>Print Items</h2>
                    <p className="text-muted">
                        Manage the global library of print items. Use <strong>Add to Catalog</strong> to make an item available on
                        {currentSubsite?.subsiteName ? <> the <strong>{currentSubsite.subsiteName}</strong> subsite</> : " the current subsite"}
                        {" "}under the <strong>Print</strong> category.
                    </p>
                </Col>
                <Col xs="auto">
                    <Link to="/admin/printitems/add">
                        <Button variant="primary">
                            <PlusCircle className="me-2" />
                            Add Print Item
                        </Button>
                    </Link>
                </Col>
            </Row>

            {error && (
                <div className="alert alert-danger">{error}</div>
            )}

            {printItems.length === 0 ? (
                <Card className="text-center py-5">
                    <Card.Body>
                        <Image size={48} className="text-muted mb-3" />
                        <h5>No Print Items Found</h5>
                        <p className="text-muted">
                            Get started by adding your first print item.
                        </p>
                        <Link to="/admin/printitems/add">
                            <Button variant="primary">Add Print Item</Button>
                        </Link>
                    </Card.Body>
                </Card>
            ) : (
                <Table striped bordered hover responsive>
                    <thead>
                        <tr>
                            <th style={{ width: '80px' }}>Image</th>
                            <th>Title</th>
                            <th>Code</th>
                            <th>Pricing</th>
                            <th>Price</th>
                            <th>Status</th>
                            <th style={{ width: '180px' }}>Catalog</th>
                            <th style={{ width: '150px' }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {printItems.map(item => (
                            <tr key={item.id}>
                                <td>
                                    {item.imageUrl ? (
                                        <img
                                            src={getImageUrl(item.imageUrl)}
                                            alt={item.title}
                                            style={{ width: 60, height: 60, objectFit: 'cover' }}
                                        />
                                    ) : (
                                        <div style={{ width: 60, height: 60, backgroundColor: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <Image size={24} className="text-muted" />
                                        </div>
                                    )}
                                </td>
                                <td>
                                    <strong>{item.title}</strong>
                                    {item.description && (
                                        <div className="text-muted small" style={{ maxWidth: 300 }}>
                                            {item.description.substring(0, 100)}
                                            {item.description.length > 100 && '...'}
                                        </div>
                                    )}
                                    {item.print_item_images?.length > 0 && (
                                        <Badge bg="info" className="mt-1">
                                            +{item.print_item_images.length} images
                                        </Badge>
                                    )}
                                </td>
                                <td>{item.code || '-'}</td>
                                <td>
                                    <Badge bg={item.pricingType === 2 ? 'primary' : item.pricingType === 3 ? 'warning' : 'secondary'}>
                                        {getPricingTypeLabel(item.pricingType)}
                                    </Badge>
                                </td>
                                <td>{formatPriceDisplay(item)}</td>
                                <td>
                                    <Badge bg={item.active ? 'success' : 'secondary'}>
                                        {item.active ? 'Active' : 'Inactive'}
                                    </Badge>
                                </td>
                                <td>
                                    {item.inCatalog ? (
                                        <Button
                                            variant="outline-success"
                                            size="sm"
                                            disabled={catalogBusyId === item.id}
                                            onClick={() => handleToggleCatalog(item)}
                                        >
                                            <CartDash className="me-1" />
                                            {catalogBusyId === item.id ? 'Removing...' : 'Remove'}
                                        </Button>
                                    ) : (
                                        <Button
                                            variant="outline-primary"
                                            size="sm"
                                            disabled={catalogBusyId === item.id}
                                            onClick={() => handleToggleCatalog(item)}
                                        >
                                            <CartPlus className="me-1" />
                                            {catalogBusyId === item.id ? 'Adding...' : 'Add to Catalog'}
                                        </Button>
                                    )}
                                </td>
                                <td>
                                    <Link to={`/admin/printitems/edit/${item.id}`}>
                                        <Button variant="outline-primary" size="sm" className="me-2">
                                            <PencilSquare />
                                        </Button>
                                    </Link>
                                    <Button
                                        variant="outline-danger"
                                        size="sm"
                                        onClick={() => handleDeleteClick(item)}
                                    >
                                        <Trash />
                                    </Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            )}

            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Delete</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to delete "{itemToDelete?.title}"? This action cannot be undone.
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={confirmDelete}>
                        Delete
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default AdminPrintItems;
